/* eslint-disable react/no-danger */
import { bugsnag } from 'shared/utils/services/bugsnag';
import Head from 'next/head';
import router from 'next/router';
import { Space } from '@goatapp/web-content-components';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Keyboard, Lazy, Mousewheel, Navigation, Pagination } from 'swiper/core';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BrandedContent } from 'brand/components/BrandContent';
import { BrandTabButtons } from 'homepage/components/BrandTabButtons';
import { Checkmark } from 'shared/icons/Checkmark';
import { computeSpaceDimensions } from 'brand/utils/computeSpaceDimensions';
import {
  BRAND_VIEW_SECTION_TYPES,
  DEFAULT_ASSET_HEIGHT_BEFORE_UI_RENDERS,
  DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS,
} from 'homepage/enums/contentConstants';
import { DESKTOP_PADDING, MOBILE_PADDING } from 'homepage/styles/constants';
import { DESKTOP_HEADER_HEIGHT, NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { DESKTOP_BANNER_HEIGHT } from 'banner/enums/constants';
import { fadeToTextAnimation } from 'styles/theme/animation';
import { FeatureFlags } from 'featureFlags/constants';
import { filterSections } from 'brand/utils/filterSections';
import { getBreadcrumbSchema } from 'productTemplate/utils/getBreadcrumbSchema';
import { GridTab } from 'brand/components/GridTab';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { navigationThemes } from 'styles/theme/navigation';
import { pickThemeByColorContrast } from 'shared/utils/pickThemeByColorContrast';
import { variableFontRegular } from 'styles/theme/text';
import { PSG_BLACK_BUTTONS } from 'brand/enum/psg-black-buttons';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import {
  setIsFooterVisible,
  setIsHeaderVisible,
  setToggleCurrentTheme,
} from 'store/slice/pageLayoutSlice';
import { startCase } from 'shared/utils/startCase';
import {
  submitBrandFollowRequest,
  submitBrandUnFollowRequest,
  getBrandFollowRequest,
} from 'brand/services/brandApi';
import { openLoginModal } from 'store/slice/accountModalSlice';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useForceHideBrandCollection } from 'brand/hooks/useForceHideBrandCollection';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { ISpaceArticleSectionFeed } from 'shared/types/SpaceArticleSection';
import { t } from 'localization/utils/t';

import 'swiper/swiper-bundle.min.css';
import ProductSearchGrid from 'shared/components/product-grid';
import { omit } from 'lodash/fp';
import { usePageMetadataCS } from 'shared/hooks/usePageMetadataCS';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import { CONSUMER_SEARCH_BRANDS_REDIRECTS } from 'brand/enum/consumerSearchRedirects';

const TP = 'brand.components.BrandStoryLanding';

SwiperCore.use([Lazy, Mousewheel, Navigation, Pagination, Keyboard]);

export interface IBrandStoryLanding {
  article: ISpaceArticleSectionFeed;
  backgroundColor: string;
  brandName?: string;
  breadcrumbs?: IBreadcrumb[];
  browseKey: string;
  browseValue: string;
  collectionDisplayName?: string;
  constructorResponse: IConstructorBrowseResponse;
  category?: string;
  description?: string;
  enableButtons?: boolean;
  displayName: string;
  globalPricingId: string;
  hasBreadcrumbSchema?: boolean;
  hasLink?: boolean;
  hideCollection?: boolean;
  isInApp?: boolean;
  linkDestination?: string;
  linkTitle?: string;
  metadataDescription?: string;
  metaTitle?: string;
  showCollection: string;
  silhouette?: string;
  isCrawler?: boolean;
  consumerSearchFilters?: any;
  consumerSearchInitialData?: any;
}

export const BrandStoryLanding = ({
  article,
  backgroundColor,
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  collectionDisplayName,
  constructorResponse,
  description = null,
  displayName,
  enableButtons = false,
  globalPricingId,
  hasBreadcrumbSchema = false,
  hasLink,
  hideCollection = false,
  isInApp = false,
  linkDestination,
  linkTitle,
  metadataDescription,
  metaTitle,
  showCollection,
  silhouette,
  isCrawler = false,
  consumerSearchFilters = {},
  consumerSearchInitialData = {},
}: IBrandStoryLanding) => {
  const storeDispatch = useStoreDispatch();

  const overlayRef = useRef<HTMLDivElement>();
  const isPSGPrevNextButtonsEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PSG_PREV_NEXT_BUTTONS,
  );

  const newPerformanceSpaceFlag = useFeatureFlag(FeatureFlags.TEMP_WEB_NEW_PERFORMANCE_SPACE);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const aspectRatio = article?.sections?.[0]?.articles?.[0]?.sections?.[0]?.aspectRatio;
  const brandId = constructorResponse?.response?.collection?.data?.brand_id;
  const metadataImage =
    constructorResponse?.response?.collection?.data?.header_asset?.type === 'picture' &&
    constructorResponse?.response?.collection?.data?.header_asset?.url;
  const mobilelAspectRatio =
    article?.sections?.[0]?.articles?.[1]?.sections?.[0]?.aspectRatio || aspectRatio;

  const MAX_LOADABLE_RANGE = 2;

  const { isMobile, windowSize } = useWindowSize();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const isMounted = useIsMounted();

  const metadataCS = usePageMetadataCS({
    consumerSearchData: consumerSearchInitialData,
    mainTitle: metaTitle,
    mainDescription: metadataDescription,
    defaultTitle: t(`${TP}.metaTitle`, 'Buy {displayName} ', {
      displayName,
    }),
  });

  const metadataLegacy = usePageMetadata({
    constructorResponse,
    mainTitle: metaTitle,
    mainDescription: metadataDescription,
    defaultTitle: t(`${TP}.metaTitle`, 'Buy {displayName} ', {
      displayName,
    }),
  });

  const { pageTitle, pageDescription } = isNewSearchPageEnabled ? metadataCS : metadataLegacy;

  const [canvasHeight, setCanvasHeight] = useState(DEFAULT_ASSET_HEIGHT_BEFORE_UI_RENDERS);
  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS);
  const [currentScroll, setCurrentScroll] = useState(0);
  // eslint-disable-next-line prefer-const
  let [displayClosedTab, setDisplayClosedTab] = useState(false);
  const [firstVideo, setIsFirstVideo] = useState(null);
  const [pendingBrandFollow, setPendingBrandFollow] = useState(false);
  const [renderSpaces, setRenderSpaces] = useState(false);
  const [scrollingDown, setScrollingDown] = useState(false);
  // eslint-disable-next-line prefer-const
  let [showBrandStoryOverlay, setShowBrandStoryOverlay] = useState(
    showCollection === 'false' || !showCollection,
  );
  const [showNotifyCopy, setShowNotifyCopy] = useState(false);
  const [swiper, setSwiper] = useState<SwiperCore>(null);
  const [swiperActiveIndex, setSwiperActiveIndex] = useState(0);
  const [userFollowsThisBrand, setUserFollowsThisBrand] = useState(false);

  const isBannerVisible = useIsBannerVisible();

  const forceHideBrandCollection = useForceHideBrandCollection();
  if (forceHideBrandCollection) {
    hideCollection = true;
    showBrandStoryOverlay = true;
    displayClosedTab = false;
  }

  const { id, uuid } = article?.sections?.[0] || {};

  const handleScrollBrandStory = (e) => {
    if (isMounted()) {
      if (e.target.scrollTop > currentScroll) {
        setScrollingDown(true);
      } else {
        setScrollingDown(false);
      }
      setCurrentScroll(e.target.scrollTop);
    }
  };

  const handleGetIfBrandFollowed = async () => {
    try {
      const response = await getBrandFollowRequest(brandId);

      if (response?.follow?.brandFollow?.brandId === brandId) {
        setUserFollowsThisBrand(true);
      }
    } catch (error) {
      bugsnag(error, { context: 'Getting User Brand Follow' });
    }
  };

  const handleMobileScrollBrandStory = () => {
    if (isMounted()) {
      if (window.scrollY > currentScroll && window.scrollY !== 0) {
        setScrollingDown(true);
      } else {
        setScrollingDown(false);
      }
      setCurrentScroll(window.scrollY);
    }
  };

  const redirectToArticleAssetUrl = (url: string) => {
    if (!url) {
      return;
    }
    try {
      /*
       * iOS/android listen for route changes in web views as a means to takeover
       * and redirect to a native view. Using router was pushing shallow redirects
       * and at times not updating the history of the page so they couldn't listen
       * for the events consistently. Using window ensured the history would update
       * and they're now able to takeover the redirect in the web views
       */
      if (url?.includes('https://')) {
        window.location.href = url;
        return;
      }
      window.location.href = `/${url}${isInApp ? '?app=y' : ''}`;
    } catch (error) {
      bugsnag(error, {
        context: 'Failed to route when triggered by Space component on brand landing story',
      });
    }
  };

  const handleFollowBrandClick = async () => {
    try {
      if (!isAuthenticated) {
        setPendingBrandFollow(true);
        storeDispatch(openLoginModal());
        return;
      }
      if (userFollowsThisBrand) {
        const response = await submitBrandUnFollowRequest(brandId);

        if (response) {
          setUserFollowsThisBrand(false);
        }
      } else {
        const response = await submitBrandFollowRequest(brandId);
        sendTrackingEvent('FOLLOW_TAP', {
          brand: brandName,
          service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
        });

        if (response) {
          setShowNotifyCopy(true);
          setUserFollowsThisBrand(true);
          setTimeout(() => {
            setShowNotifyCopy(false);
          }, 2000);
        }
      }
    } catch (error) {
      bugsnag(error, { context: 'Failed attempt to follow/unfollow a brand' });
    }
  };

  const handleSlideChange = (swipercore) => {
    const { activeIndex } = swipercore;
    setSwiperActiveIndex(activeIndex);
  };

  const handleClickNext = () => {
    if (swiperActiveIndex < article?.sections?.length - 1) {
      swiper.slideTo(swiperActiveIndex + 1);
    }
  };

  const handleClickPrevious = () => {
    if (swiperActiveIndex > 0) {
      swiper.slideTo(swiperActiveIndex - 1);
    }
  };

  useEffect(() => {
    if (isMounted()) {
      try {
        if (swiper?.params) {
          swiper?.update();
          swiper?.updateAutoHeight();
        }
      } catch (error) {
        bugsnag(error, { context: 'Error resizing swiper of spaces' });
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMounted() && windowSize?.width && swiper?.params) {
        try {
          const { height, width }: { height: number; width: number } = computeSpaceDimensions(
            windowSize?.height,
            windowSize?.width,
            isMobile ? mobilelAspectRatio : aspectRatio,
          );
          setCanvasHeight(height + 1);
          setCanvasWidth(width);
          swiper?.update();
          swiper?.updateAutoHeight();
          swiper?.updateSize();
        } catch (error) {
          bugsnag(error, { context: 'Error resizing swiper of spaces' });
        }
      }
    };

    document.addEventListener('resize', handleResize);
    router.events.on('routeChangeComplete', handleResize);

    return () => {
      document.removeEventListener('resize', handleResize);
      router.events.off('routeChangeComplete', handleResize);
    };
  });

  useEffect(() => {
    if (isMounted()) {
      if (isAuthenticated && brandId) {
        if (pendingBrandFollow) {
          void handleFollowBrandClick();
        } else {
          void handleGetIfBrandFollowed();
          setPendingBrandFollow(false);
        }
      }
      setRenderSpaces(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isMobile && isMounted()) {
      window.addEventListener('scroll', handleMobileScrollBrandStory, { passive: false });
      return () => {
        window.removeEventListener('scroll', handleMobileScrollBrandStory);
      };
    }
  }, [isMobile, currentScroll]);

  useEffect(() => {
    if (isMounted()) {
      storeDispatch(setIsFooterVisible(false));

      if (windowSize?.width) {
        try {
          const { height, width }: { height: number; width: number } = computeSpaceDimensions(
            windowSize?.height,
            windowSize?.width,
            isMobile ? mobilelAspectRatio : aspectRatio,
          );
          setCanvasHeight(height + 1);
          setCanvasWidth(width);
        } catch (error) {
          bugsnag(error, { context: 'Error resizing swiper of spaces' });
        }
      }
    }
  }, [isMobile, windowSize]);

  useEffect(() => {
    if (!showBrandStoryOverlay) {
      router.query.showCollection = 'true';
      void router.push(router);
    } else {
      document.body.style.overflow = 'scroll';
    }
  }, [showBrandStoryOverlay]);

  useEffect(() => {
    if (isMounted()) {
      setShowBrandStoryOverlay(showCollection === 'false' || !showCollection);
      setDisplayClosedTab(!(showCollection === 'false' || !showCollection));
    }
  }, [showCollection]);

  useEffect(() => {
    try {
      const newTheme = {
        ...navigationThemes?.[pickThemeByColorContrast(backgroundColor)],
      };
      newTheme.backgroundColor = backgroundColor;
      newTheme.headerBackgroundColor = backgroundColor;
      newTheme.mainBorderColor = !isMobile && 'transparent';
      storeDispatch(setToggleCurrentTheme(newTheme));
    } catch (error) {
      bugsnag(error, { context: 'Failed attempt to set dynamic theme' });
    }
  });

  const CORRECT_SLUG_NAME =
    CONSUMER_SEARCH_BRANDS_REDIRECTS[slugify(brandName)] || slugify(brandName);

  const fashionSchema = [
    {
      id: `/brand/${CORRECT_SLUG_NAME}`,
      name: startCase(brandName),
    },
    {
      id: `/apparel/brand/${CORRECT_SLUG_NAME}`,
      name: brandName
        ? t(`${TP}.ldJsonFashionSchema`, '{brand} Apparel', {
            brand: String(startCase(brandName)),
          })
        : null,
    },
  ];
  const sneakerSchema = [
    {
      id: `/brand/${CORRECT_SLUG_NAME}`,
      name: startCase(brandName),
    },
    {
      id: `/sneakers/brand/${CORRECT_SLUG_NAME}`,
      name: brandName
        ? t(`${TP}.ldJsonSneakerSchema`, '{brand} Sneakers', {
            brand: String(startCase(brandName)),
          })
        : null,
    },
    {
      id: `/sneakers/silhouette/${slugify(silhouette)}`,
      name: startCase(silhouette),
    },
  ];

  const filteredArticleSections = filterSections(article?.sections, isMobile);
  // aDDING consumer search

  const toggleHeader = (status) => {
    storeDispatch(setIsHeaderVisible(status));
  };

  const allCSFilters = useMemo(() => {
    return omit('collectionSlug', consumerSearchFilters);
  }, [consumerSearchFilters]);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="og:title" />
        <meta name="twitter:title" content={pageTitle} key="twitter:title" />
        <meta
          name="description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="description"
        />
        <meta
          property="og:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="og:description"
        />
        <meta
          name="twitter:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="twitter:description"
        />
        {metadataImage && (
          <>
            <meta name="twitter:image" content={metadataImage} key="twitter:image" />
            <meta property="og:image" content={metadataImage} key="og:image" />
          </>
        )}
        {hasBreadcrumbSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: getBreadcrumbSchema(
                category === 'sneakers' ? sneakerSchema : fashionSchema,
                router.locale,
              ),
            }}
          />
        )}
      </Head>
      <BrandStoryLanding.Container
        $backgroundColor={backgroundColor}
        $isVisible={showBrandStoryOverlay}
        $isBannerVisible={isBannerVisible}
        id={`brand-story-container-${uuid || id}`}
        ref={overlayRef}
        onScroll={handleScrollBrandStory}
      >
        <BrandStoryLanding.HexagonTop $isBannerVisible={isBannerVisible} />
        {renderSpaces && canvasWidth && (
          <BrandStoryLanding.SpaceContainer
            id="space-container"
            $swiperView={filteredArticleSections.length > 1}
          >
            {browseValue === 'paris-saint-germain' && isPSGPrevNextButtonsEnabled && (
              <>
                <BrandStoryLanding.LeftButton $isVisible={swiperActiveIndex > 0}>
                  <BrandStoryLanding.Image
                    alt="indicator there is a previous view"
                    className="swiper-button-prev"
                    src={`/images/previous_${
                      (PSG_BLACK_BUTTONS[swiperActiveIndex] && 'dark') || `light`
                    }.png`}
                  />
                </BrandStoryLanding.LeftButton>
                <BrandStoryLanding.RightButton
                  $isVisible={swiperActiveIndex < filteredArticleSections?.length - 1}
                >
                  <BrandStoryLanding.Image
                    alt="indicator there is a next view"
                    className="swiper-button-next"
                    src={`/images/next_${
                      (PSG_BLACK_BUTTONS[swiperActiveIndex] && 'dark') || `light`
                    }.png`}
                  />
                </BrandStoryLanding.RightButton>
              </>
            )}
            {enableButtons && (
              <BrandTabButtons
                handleClickNext={handleClickNext}
                handleClickPrevious={handleClickPrevious}
                numOfHeroes={filteredArticleSections?.length}
                swiper={swiper}
                swiperActiveIndex={swiperActiveIndex}
                isBrandPage
              />
            )}
            {filteredArticleSections?.length > 1 ? (
              <Swiper
                className="mySwiper"
                direction="horizontal"
                initialSlide={0}
                lazy={{
                  checkInView: true,
                  loadOnTransitionStart: true,
                }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                modules={[Lazy, Pagination, Mousewheel, Navigation]}
                mousewheel
                keyboard={{
                  enabled: true,
                }}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
                centeredSlides
                onSlideChange={handleSlideChange}
                onSwiper={(s) => setSwiper(s)}
                slidesPerView="auto"
                touchStartPreventDefault={false}
                speed={600}
              >
                {filteredArticleSections?.map((section, index) =>
                  // eslint-disable-next-line no-nested-ternary
                  section?.type === BRAND_VIEW_SECTION_TYPES.VIDEO_HERO_SECTION ? (
                    <SwiperSlide id={`video-${index}`} key={section?.uuid || section?.id}>
                      <BrandedContent
                        key={section?.uuid || section?.id}
                        drawerName="brand-story"
                        {...section}
                        index={index}
                        firstVideo={firstVideo}
                        setFirstVideo={setIsFirstVideo}
                        canvasHeight={750}
                        canvasWidth={canvasWidth}
                      />
                    </SwiperSlide>
                  ) : isMobile && section?.articles?.length > 1 ? (
                    section?.articles?.[1]?.sections?.map((space) => (
                      <SwiperSlide id={`space-${index}`} key={space?.id}>
                        <BrandStoryLanding.SwiperMinSizeContainer>
                          {Math.abs(swiper?.activeIndex - index) < MAX_LOADABLE_RANGE ? (
                            <Space
                              space={space}
                              maxHeight={canvasHeight}
                              maxWidth={canvasWidth}
                              virtualMaxHeight={canvasHeight}
                              virtualMaxWidth={canvasWidth}
                              onItemLinkClick={redirectToArticleAssetUrl}
                              usePerformanceSpace={newPerformanceSpaceFlag}
                            />
                          ) : (
                            <BrandStoryLanding.TempSpaceHolder
                              $height={canvasHeight}
                              $width={canvasWidth}
                            />
                          )}
                        </BrandStoryLanding.SwiperMinSizeContainer>
                      </SwiperSlide>
                    ))
                  ) : (
                    section?.articles?.[0]?.sections?.map((space) => (
                      <SwiperSlide id={`space-${index}`} key={space?.id}>
                        <BrandStoryLanding.SwiperMinSizeContainer>
                          {Math.abs(swiper?.activeIndex - index) < MAX_LOADABLE_RANGE ? (
                            <Space
                              space={space}
                              maxHeight={canvasHeight}
                              maxWidth={canvasWidth}
                              virtualMaxHeight={canvasHeight}
                              virtualMaxWidth={canvasWidth}
                              onItemLinkClick={redirectToArticleAssetUrl}
                              usePerformanceSpace={newPerformanceSpaceFlag}
                            />
                          ) : (
                            <BrandStoryLanding.TempSpaceHolder
                              $height={canvasHeight}
                              $width={canvasWidth}
                            />
                          )}
                        </BrandStoryLanding.SwiperMinSizeContainer>
                      </SwiperSlide>
                    ))
                  ),
                )}
              </Swiper>
            ) : (
              filteredArticleSections?.map((section) =>
                isMobile && section?.articles?.length > 1
                  ? section?.articles?.[1]?.sections?.map((space) => (
                      <Space
                        space={space}
                        maxHeight={canvasHeight}
                        maxWidth={canvasWidth}
                        virtualMaxHeight={canvasHeight}
                        virtualMaxWidth={canvasWidth}
                        onItemLinkClick={redirectToArticleAssetUrl}
                        usePerformanceSpace={newPerformanceSpaceFlag}
                      />
                    ))
                  : section?.articles?.[0]?.sections?.map((space) => (
                      <Space
                        space={space}
                        maxHeight={canvasHeight}
                        maxWidth={canvasWidth}
                        virtualMaxHeight={canvasHeight}
                        virtualMaxWidth={canvasWidth}
                        onItemLinkClick={redirectToArticleAssetUrl}
                        usePerformanceSpace={newPerformanceSpaceFlag}
                      />
                    )),
              )
            )}
          </BrandStoryLanding.SpaceContainer>
        )}
        <BrandStoryLanding.FixedBottomDataContainer
          $hide={scrollingDown && article?.sections?.length <= 1}
        >
          {!isMobile && (
            <BrandStoryLanding.ActionButton
              onClick={handleFollowBrandClick}
              $canFollow={!!brandId && brandId !== '0'}
            >
              {userFollowsThisBrand &&
                t(`${TP}.followingBrand`, `Following {brandName}`, {
                  brandName,
                })}
              {!userFollowsThisBrand && (
                <BrandStoryLanding.TextItems>
                  <span>{t(`${TP}.forUpdates`, `For Exclusive Updates`)}</span>
                  <span>
                    {t(`${TP}.followBrand`, `Follow {brandName}`, {
                      brandName,
                    })}
                  </span>
                </BrandStoryLanding.TextItems>
              )}
              {userFollowsThisBrand && (
                <BrandStoryLanding.Checkmark
                  color={pickThemeByColorContrast(backgroundColor) === 'light' ? 'black' : 'white'}
                />
              )}
            </BrandStoryLanding.ActionButton>
          )}
          <BrandStoryLanding.CenterCopyContainer>
            <BrandStoryLanding.NotifyCopy $isVisible={showNotifyCopy}>
              {t(`${TP}.brandNotify`, `We’ll notify you for new releases, news, and more.`)}
            </BrandStoryLanding.NotifyCopy>

            <BrandStoryLanding.BrandName $isVisible={false}>
              {displayName?.toUpperCase()}
            </BrandStoryLanding.BrandName>
          </BrandStoryLanding.CenterCopyContainer>
          {!isInApp ? (
            <BrandStoryLanding.IncreaseHoverRadiusForCustomCursor
              onMouseEnter={() => !hideCollection && setDisplayClosedTab(true)}
            >
              {isMobile && (
                <BrandStoryLanding.ActionButton
                  onClick={handleFollowBrandClick}
                  $canFollow={!!brandId && brandId !== '0'}
                >
                  {userFollowsThisBrand
                    ? t(`${TP}.followingBrand`, `Following {brandName}`, {
                        brandName,
                      })
                    : t(`${TP}.followBrand`, `Follow {brandName}`, {
                        brandName,
                      })}
                  {userFollowsThisBrand && (
                    <BrandStoryLanding.Checkmark
                      color={
                        pickThemeByColorContrast(backgroundColor) === 'light' ? 'black' : 'white'
                      }
                    />
                  )}
                </BrandStoryLanding.ActionButton>
              )}
              <BrandStoryLanding.ActionButton
                $canFollow={!hideCollection}
                onClick={() => !hideCollection && setShowBrandStoryOverlay(false)}
                onMouseEnter={() => !hideCollection && setDisplayClosedTab(true)}
              >
                {!isMobile
                  ? t(`${TP}.collectioNameDisplay`, `{collectionDisplayName} Collection`, {
                      collectionDisplayName,
                    })
                  : t(`${TP}.shopCollection`, `Shop {collectionDisplayName}`, {
                      collectionDisplayName,
                    })}
              </BrandStoryLanding.ActionButton>
            </BrandStoryLanding.IncreaseHoverRadiusForCustomCursor>
          ) : (
            <BrandStoryLanding.ActionLink
              $canFollow={!hideCollection}
              href={`/collections/${browseValue}`}
            >
              {!isMobile
                ? t(`${TP}.collectioNameDisplay`, `{collectionDisplayName} Collection`, {
                    collectionDisplayName,
                  })
                : t(`${TP}.shopCollection`, `Shop {collectionDisplayName}`, {
                    collectionDisplayName,
                  })}
            </BrandStoryLanding.ActionLink>
          )}
        </BrandStoryLanding.FixedBottomDataContainer>
      </BrandStoryLanding.Container>

      {!isMobile ? (
        <GridTab
          backgroundColor={backgroundColor}
          collectionName={
            !isMobile
              ? t(`${TP}.collectioNameDisplay`, `{collectionDisplayName} Collection`, {
                  collectionDisplayName,
                })
              : t(`${TP}.shopCollection`, `Shop {collectionDisplayName}`, {
                  collectionDisplayName,
                })
          }
          showBrandStoryOverlay={showBrandStoryOverlay}
          setShowBrandStoryOverlay={setShowBrandStoryOverlay}
          setShowTab={setDisplayClosedTab}
          showTab={displayClosedTab}
        >
          <BrandStoryLanding.TabContentContainer>
            {!isNewSearchPageEnabled ? (
              <SearchGrid
                brandStoryVisible={showBrandStoryOverlay}
                breadcrumbs={breadcrumbs}
                browseKey={browseKey}
                browseValue={browseValue}
                constructorResponse={constructorResponse}
                description={description}
                globalPricingId={globalPricingId}
                hasLink={hasLink}
                hideMetadata
                linkDestination={linkDestination}
                linkTitle={linkTitle}
                title={displayName}
                isCrawler={isCrawler}
              />
            ) : (
              <ProductSearchGrid
                toggleHeader={toggleHeader}
                initialData={consumerSearchInitialData}
                collectionSlug={consumerSearchFilters?.collectionSlug || ''}
                collectionDetails={undefined}
                isCrawler={isCrawler}
                pageFilters={allCSFilters}
                pageAttributes={{
                  theme: 'dark',
                  brandStoryVisible: showBrandStoryOverlay,
                  hasLink,
                  linkTitle,
                  title: displayName,
                  description,
                  globalPricingId,
                  breadcrumbs,
                  hideMetadata: true,
                }}
              />
            )}
          </BrandStoryLanding.TabContentContainer>
        </GridTab>
      ) : (
        <div>
          {!showBrandStoryOverlay &&
            (!isNewSearchPageEnabled ? (
              <SearchGrid
                brandStoryVisible={showBrandStoryOverlay}
                breadcrumbs={breadcrumbs}
                browseKey={browseKey}
                browseValue={browseValue}
                constructorResponse={constructorResponse}
                description={description}
                globalPricingId={globalPricingId}
                hasLink={hasLink}
                hideMetadata
                linkDestination={linkDestination}
                linkTitle={linkTitle}
                title={displayName}
                isCrawler={isCrawler}
              />
            ) : (
              <ProductSearchGrid
                toggleHeader={toggleHeader}
                initialData={consumerSearchInitialData}
                collectionDetails={undefined}
                collectionSlug={consumerSearchFilters?.collectionSlug || ''}
                isCrawler={isCrawler}
                pageFilters={allCSFilters}
                pageAttributes={{
                  brandStoryVisible: showBrandStoryOverlay,
                  hasLink,
                  linkTitle,
                  title: displayName,
                  description,
                  globalPricingId,
                  breadcrumbs,
                  hideMetadata: true,
                  theme: 'dark',
                }}
              />
            ))}
        </div>
      )}
    </>
  );
};

BrandStoryLanding.Container = styled.div<{
  $backgroundColor?: string;
  $isBannerVisible: boolean;
  $isVisible: boolean;
}>`
  align-items: center;
  bottom: -2px;
  display: block;
  opacity: 1;
  width: 100%;
  transition: all 0.3s ease-in 0s;
  z-index: ${({ theme }) => theme.zIndex.brandOverlay};
  ${hideScrollbars};
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    ${({ $isVisible }) =>
      !$isVisible &&
      `
     display: none;
     position: fixed;
     `}
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: flex;
    max-height: ${({ $isBannerVisible }) =>
      $isBannerVisible
        ? `calc(100vh - ${NEW_NAV_MOBILE_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`
        : `calc(100vh - ${NEW_NAV_MOBILE_HEADER_HEIGHT})`};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible
        ? `calc(100vh - ${NEW_NAV_MOBILE_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`
        : `calc(100vh - ${NEW_NAV_MOBILE_HEADER_HEIGHT})`};
    flex-direction: column;
    justify-content: center;
    overflow-y: scroll;
    position: fixed;
    &::before {
      background-color: ${({ theme, $backgroundColor }) =>
        $backgroundColor || theme.navigation.backgroundColor};
      border-right: 1px solid ${({ theme }) => theme.navigation.borderColor};
      border-bottom: none;
      content: '';
      height: 85px;
      left: -14px;
      position: fixed;
      top: ${({ $isBannerVisible }) =>
        $isBannerVisible ? `calc(8px + ${DESKTOP_BANNER_HEIGHT})` : '8px'};
      transform: rotate(18deg);
      transform-origin: top left;
      width: 42px;
      z-index: 900;
    }
    &::after {
      background-color: ${({ theme, $backgroundColor }) =>
        $backgroundColor || theme.navigation.backgroundColor};
      border-left: 1px solid ${({ theme }) => theme.navigation.borderColor};
      content: '';
      height: 76px;
      position: fixed;
      right: 1px;
      top: ${({ $isBannerVisible }) =>
        $isBannerVisible ? `calc(12px + ${DESKTOP_BANNER_HEIGHT})` : '12px'};
      transform: rotate(-34.2deg);
      transform-origin: top right;
      width: 48px;
      z-index: 900;
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    max-height: ${({ $isBannerVisible }) =>
      $isBannerVisible
        ? `calc(100vh - ${DESKTOP_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`
        : `calc(100vh - ${DESKTOP_HEADER_HEIGHT})`};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible
        ? `calc(100vh - ${DESKTOP_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`
        : `calc(100vh - ${DESKTOP_HEADER_HEIGHT})`};
  }
`;

BrandStoryLanding.FixedBottomDataContainer = styled.div<{
  $hide: boolean;
}>`
  align-items: center;
  bottom: ${({ $hide }) => ($hide ? '-50px' : '24px')};
  display: flex;
  height: 30px;
  flex-direction: row;
  justify-content: space-between;
  left: ${MOBILE_PADDING};
  right: ${MOBILE_PADDING};
  position: fixed;
  transition: all 0.3s ease-in 0s;
  z-index: ${({ theme }) => theme.zIndex.brandOverlay};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    align-items: flex-end;
    bottom: ${({ $hide }) => ($hide ? '-50px' : '32px')};
    left: ${DESKTOP_PADDING};
    right: ${DESKTOP_PADDING};
  }
`;

BrandStoryLanding.ActionButton = styled.button<{ $canFollow: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 0.5px solid ${({ theme }) => theme.navigation.textColor};
  cursor: pointer;
  display: flex;
  justify-content: center;
  margin-top: 8px;
  max-height: 17px;
  padding: 3px;
  text-transform: uppercase;
  opacity: ${({ $canFollow }) => ($canFollow ? '1' : '0 ')};
  z-index: ${({ theme }) => theme.zIndex.actionButton};
  overflow: hidden;

  ${variableFontRegular({ size: '9.5px', height: '9px' })}
`;

BrandStoryLanding.ActionLink = styled.a<{ $canFollow: boolean }>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 0.5px solid ${({ theme }) => theme.navigation.textColor};
  cursor: pointer;
  max-height: 17px;
  padding: 3px;
  text-transform: uppercase;
  opacity: ${({ $canFollow }) => ($canFollow ? '1' : '0 ')};
  z-index: ${({ theme }) => theme.zIndex.actionButton};
  overflow: hidden;

  ${variableFontRegular({ size: '9.5px', height: '9px' })}
`;

BrandStoryLanding.CenterCopyContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
`;

BrandStoryLanding.Checkmark = styled(Checkmark)`
  margin-left: 4px;
`;

BrandStoryLanding.TextItems = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  opacity: 0;

  animation-name: ${fadeToTextAnimation};
  animation-duration: 5.5s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: normal;
  animation-delay: 1s;

  span {
    margin: 4px;
  }

  &:hover {
    transform: translate3d(0, -9px, 0);
    opacity: 1;
    animation: none;
  }
`;

BrandStoryLanding.DescriptionDiv = styled.div`
  max-width: 200px;
`;

BrandStoryLanding.BrandName = styled.h1<{ $isVisible: boolean }>`
  ${variableFontRegular({ size: '18px', height: '18px' })}

  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};

  ${({ $isVisible }) =>
    $isVisible ? 'position: relative;' : 'position: absolute; top: 0; left:0;'};
  transition: all 0.7s ease-out;
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    bottom: -16px;
    min-height: 36px;
    margin-top: 28px;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    ${variableFontRegular({ size: '32px', height: '13px' })}
  }
`;

BrandStoryLanding.NotifyCopy = styled.span<{ $isVisible: boolean }>`
  ${variableFontRegular({ size: '9.5px' })}

  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  ${({ $isVisible }) =>
    $isVisible ? 'position: relative;' : 'position: absolute; top: 0; left:0;'};
  transition: all 0.3s ease-out;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    bottom: -16px;
    min-height: 36px;
    margin-top: 28px;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    ${variableFontRegular({ size: '11.5px' })}
  }
`;

BrandStoryLanding.GridContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  position: relative;
`;

BrandStoryLanding.ThreeDModelContainer = styled.div<{ $direction: string }>`
  align-items: center;
  display: flex;
  justify-content: center;
  position: fixed;
  z-index: 900;
  top: 0;
  bottom: 0;
  ${({ $direction }) => `${$direction}: 0px`};
  margin: auto;
  width: 300px;
  height: 100px;

  model-viewer {
    width: 300px;
    height: 300px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 300px;
    height: 300px;

    model-viewer {
      width: 300px;
      height: 300px;
    }
  }
`;

BrandStoryLanding.TabContentContainer = styled.div`
  height: 85vh;
  width: 100%;
  ${hideScrollbars}
`;

BrandStoryLanding.HexagonTop = styled.div<{ $isBannerVisible: boolean }>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  display: none;
  width: calc(100% - 48px);
  border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
  left: 16px;
  position: fixed;
  top: 58px;
  z-index: 1000;
  height: 4px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: block;
    width: calc(100% - 46px);
    left: 15px;
    top: ${({ $isBannerVisible }) =>
      $isBannerVisible ? `calc(${DESKTOP_BANNER_HEIGHT} + 48px)` : `55px`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    top: ${({ $isBannerVisible }) =>
      $isBannerVisible ? `calc(${DESKTOP_BANNER_HEIGHT} + 49px)` : '49px'};
  }
`;

BrandStoryLanding.SpaceContainer = styled.div<{
  $swiperView: boolean;
}>`
  width: 100vw;
  ${({ $swiperView }) =>
    !$swiperView &&
    css`
      display: flex;
      justify-content: center;
      max-width: 100vw;
    `};
  top: ${({ $swiperView }) => ($swiperView ? `58px` : `0px`)};
  ${hideScrollbars};
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    top: 0px;
  }

  position: absolute;
`;

BrandStoryLanding.SwiperMinSizeContainer = styled.div`
  align-items: flex-start;
  display: flex;
  font-size: 36px;
  height: calc(100vh - 50px);
  justify-content: center;
  min-height: 100%;
  overflow: hidden;
  width: 100vw;
  max-width: 100vw;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    align-items: center;
  }
`;

BrandStoryLanding.IncreaseHoverRadiusForCustomCursor = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 100px;
  }
`;

BrandStoryLanding.IterateButton = styled.button<{ $isVisible: boolean }>`
  bottom: 0;
  cursor: default;
  height: 100vh;
  min-width: 25%;
  opacity: 0;
  position: fixed;
  top: 0;
  width: auto;
  z-index: ${({ theme }) => theme.zIndex.arrowButton};

  &:hover {
    opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

BrandStoryLanding.RightButton = styled(BrandStoryLanding.IterateButton)`
  right: 32px;
`;

BrandStoryLanding.LeftButton = styled(BrandStoryLanding.IterateButton)`
  left: 32px;
`;

BrandStoryLanding.Image = styled.img`
  cursor: pointer;
  margin: auto;
  max-height: 16px;
  min-height: 16px;
  width: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

BrandStoryLanding.TempSpaceHolder = styled.div<{ $height: number; $width: number }>`
  min-height: ${({ $height }) => $height}px;
  min-width: ${({ $width }) => $width}px;
`;
